import * as React from 'react'
import style from './add-edit-team.css'
import {
  applications,
  AutoToggleAppsRecord,
  DependentAppsRecord,
  IApplication,
} from 'constants/user/applications'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { useStores } from 'utils/hooks/useStores'
import classNames from 'classnames'
import {
  Dropdown,
  DropdownSelectableItem,
  InputField,
  Checkbox,
  TextToggler,
  Icons,
  TableV2,
  Heading,
  IconButton,
  Button,
  Select,
} from 'plume-ui'
import { ColumnType } from 'plume-ui/dist/components/TableV2/types'
import { AccountStore } from '../account-store'
import { AddEditOption } from 'interfaces/api/portal/user-api'
import { Team } from 'interfaces/api/portal/company-api'
import { InputMessage } from 'plume-ui/dist/components/InputField/InputField'
import { TeamApplications } from './team-constants'
import { getAppFeaturesValue, getDefaultAppFeatures, isAppFeatureAvailable } from './team.helpers'
import { parseJsonSafe } from 'helpers/general-helpers'
import { RoleOption } from 'modules/dashboard/user/user-store'
import deepEqual from 'fast-deep-equal'
import { getCompanyAllowedApplications } from 'helpers/company-helpers'
import { commonStyle } from 'style/commonly-used-styles'
import { Tooltip } from 'components/tooltip'

type DependenceError = { dependent: string; dependecy: string }

type SetTeamProps = {
  onDelete?: () => void
  team?: Team
  accountStore: AccountStore
  action: AddEditOption
}

const getAdvancedRole = (
  advancedOptionsTranslation: IApplication['advancedOptionsTranslation'],
  features: string[],
) => {
  if (!features?.length) {
    return null
  }
  const featuresSorted = [...features].sort()
  for (const option of advancedOptionsTranslation) {
    const requiredArray = [...option.requiredOptions].sort()
    const areEqual = deepEqual(featuresSorted, requiredArray)
    if (areEqual) {
      return option.value
    }
  }
}

export const SetTeam = observer((props: SetTeamProps) => {
  const { accountStore, action, team } = props
  const originalTeamName = team?.name || ''
  const appStore = useStores()
  const { t } = useTranslation()
  const { teams } = accountStore
  const [isEdited, setIsEdited] = React.useState(false)
  const [teamName, setTeamName] = React.useState(originalTeamName || '')
  const [teamDescription, setTeamDescription] = React.useState(team?.description || '')
  const company = appStore?.authStore?.currentUser?.company
  const [dependenceError, setDependenceError] = React.useState<DependenceError>(null)
  const [autoToggleApplications] = React.useState<AutoToggleAppsRecord>(
    parseJsonSafe(process.env.AUTO_TOGGLE_APPS),
  )
  const [dependentApps] = React.useState<DependentAppsRecord>(
    parseJsonSafe(process.env.DEPENDENT_APPS),
  )

  const portalAppId = process.env.OKTA_CLIENT_ID

  function teamApplicationsInitializer() {
    const teamApplications = applications.reduce((prevApps, a) => {
      const companyTeamApp = team?.apps?.find(da => da.id === a.id)
      const teamApplication: TeamApplications = {
        [a.id]: {
          selected: !!companyTeamApp || null,
          role: companyTeamApp?.role || null,
          features: companyTeamApp?.features || getDefaultAppFeatures(a, company),
          showAdvanced: a.advancedOptionsTranslation
            ? getAdvancedRole(a.advancedOptionsTranslation, companyTeamApp?.features) === undefined
            : false,
        },
      }
      if (a.id === portalAppId) {
        teamApplication[a.id].selected = true
      }
      return { ...prevApps, ...teamApplication }
    }, {} as TeamApplications)
    return teamApplications
  }

  function teamApplicationReducer(
    prevTeamApplications: TeamApplications,
    value: {
      appId?: string
      selected?: boolean
      role?: string
      features?: string[]
      showAdvanced?: boolean
      resetTeamApps?: boolean
    },
  ) {
    if (value.resetTeamApps) {
      return teamApplicationsInitializer()
    }
    const prevApplicationState = prevTeamApplications[value.appId]
    setIsEdited(true)
    return {
      ...prevTeamApplications,
      [value.appId]: {
        selected: value.selected === undefined ? prevApplicationState?.selected : value.selected,
        role: value.role === undefined ? prevApplicationState?.role : value.role,
        features: value.features === undefined ? prevApplicationState?.features : value.features,
        showAdvanced:
          value.showAdvanced === undefined
            ? prevApplicationState?.showAdvanced
            : value.showAdvanced,
      },
    }
  }

  const [teamApplications, dispatchTeamApplications] = React.useReducer(
    teamApplicationReducer,
    null,
    teamApplicationsInitializer,
  )

  // Re-seed the form from the team's saved apps whenever the team changes OR its
  // apps finish streaming in. Apps load lazily (getTeamApps below), so team.apps
  // arrives after mount; depending on team.apps here guarantees the role/feature
  // state is re-initialized once the data lands (otherwise every role dropdown
  // stays on "Please Select"). Keying on groupId (not the whole team object) also
  // avoids wiping in-progress edits on unrelated re-renders.
  React.useEffect(() => {
    dispatchTeamApplications({ resetTeamApps: true })
    // Re-seeding means the form now matches the saved server state (initial load or
    // post-save), so clear any lingering "unsaved changes" flag.
    setIsEdited(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team?.groupId, team?.apps])

  // Load the selected team's app roles/features. The teams-list endpoint returns
  // apps as id-only (no role/features — "without profile because of okta API"), so
  // team.apps is truthy but incomplete; we must always fetch the per-team apps on
  // selection to get the roles. The reset effect above re-initializes the form once
  // they stream in.
  React.useEffect(() => {
    if (action === 'edit' && team?.groupId && team?.name) {
      accountStore.getTeamApps(team.groupId, team.name)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team?.groupId])

  const teamDuplicateNameError = teams?.find(
    d =>
      d?.name?.toLowerCase() === teamName?.toLowerCase() &&
      d?.name?.toLowerCase() !== originalTeamName?.toLowerCase(),
  )
  const teamInputFieldErrorMessages: Partial<InputMessage>[] = []
  const teamDescriptionInputFieldErrorMessages: Partial<InputMessage>[] = []
  if (!teamName) {
    teamInputFieldErrorMessages.push({ status: 'error', message: t('errors.requiredField') })
  }
  if (teamDuplicateNameError) {
    teamInputFieldErrorMessages.push({
      status: 'error',
      message: t('teams.duplicateTeamNamesNotAllowed'),
    })
  }
  const isNameValid = /^[a-zA-Z0-9]([A-Za-z0-9\-_ ]*[a-zA-Z0-9])?$/.test(teamName)
  if (!!teamName && !isNameValid) {
    teamInputFieldErrorMessages.push({
      status: 'error',
      message: t('teams.teamNameInvalid'),
    })
  }
  const isTeamNameTooLong = teamName?.length > 50
  if (isTeamNameTooLong) {
    teamInputFieldErrorMessages.push({
      status: 'error',
      message: t('errors.max50CharsAllowed'),
    })
  }

  let isRoleUnselected = false
  for (const appId in teamApplications) {
    const appInfo = applications.find(a => a.id === appId)
    if (!appInfo) {
      continue
    }
    if (
      (!!appInfo.roleOptions &&
        teamApplications[appId].selected &&
        !teamApplications[appId].role) ||
      (!!appInfo.featuresOptions &&
        teamApplications[appId].selected &&
        !teamApplications[appId]?.features?.length) ||
      (appInfo.advancedOptionsTranslation &&
        teamApplications[appId].selected &&
        !teamApplications[appId]?.showAdvanced &&
        !getAdvancedRole(appInfo.advancedOptionsTranslation, teamApplications[appId]?.features))
    ) {
      isRoleUnselected = true
      break
    }
  }

  const isTeamDescriptionTooLong = teamDescription?.length > 1000
  if (isTeamDescriptionTooLong) {
    teamDescriptionInputFieldErrorMessages.push({
      status: 'error',
      message: t('errors.maxCustomCharsAllowed', { charNumber: 1000 }),
    })
  }

  const companyAllowedApplications = getCompanyAllowedApplications(company)

  const isSubmitDisabled =
    !isEdited ||
    !!teamInputFieldErrorMessages.length ||
    isRoleUnselected ||
    isTeamNameTooLong ||
    isTeamDescriptionTooLong ||
    !!dependenceError

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    e.stopPropagation()
    if (isSubmitDisabled) {
      return
    }

    if (action === 'add') {
      props.accountStore.validateAndSubmitAddTeam(teamApplications, teamName, teamDescription)
    } else if (action === 'edit') {
      props.accountStore.validateAndSubmitEditTeam(
        teamApplications,
        teamName,
        originalTeamName,
        team.groupId,
        teamDescription,
      )
    }
  }

  const getRoleText = (role: string, roleOptions: readonly RoleOption[]) => {
    return t(roleOptions?.find(i => i.value === role)?.text)
  }

  const handleCancel = () => {
    dispatchTeamApplications({ resetTeamApps: true })
    setTeamName(originalTeamName)
    setTeamDescription(team?.description || '')
    setIsEdited(false)
  }

  let _depError: DependenceError = null
  for (const app of applications) {
    if (teamApplications[app.id]?.selected && dependentApps?.[app.id]) {
      for (const dependence of dependentApps?.[app.id]) {
        const doesDependentSatisfy =
          !dependence.dependentRole || teamApplications[app.id]?.role === dependence.dependentRole
        const isDependencyIncorrect = dependence?.allowedRoles
          ? !dependence?.allowedRoles?.includes(
              teamApplications[dependence.dependencyAppId]?.role,
            ) || !teamApplications[dependence.dependencyAppId]?.selected
          : !teamApplications[dependence.dependencyAppId]?.selected

        if (doesDependentSatisfy && isDependencyIncorrect) {
          const dependentRole = getRoleText(dependence.dependentRole, app.roleOptions) || ''
          const dependencyApplication = applications.find(a => a.id === dependence.dependencyAppId)
          const dependencyRoles = dependence.allowedRoles
            ? dependence?.allowedRoles
                ?.map(r => getRoleText(r, dependencyApplication?.roleOptions))
                ?.join(` ${t('common.or')} `)
            : ''

          _depError = {
            dependecy: `${dependencyApplication?.name} ${dependencyRoles}`,
            dependent: `${app.name} ${dependentRole}`,
          }
        }
      }
    }
  }
  if (!deepEqual(dependenceError, _depError)) {
    setDependenceError(_depError)
  }

  const appAccessData = applications.filter(
    app => !!companyAllowedApplications.find(caa => caa.id === app.id),
  )

  const stopPropagationPreventDefault = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const renderAppNameCell = (app: IApplication) => {
    const hasAdvanced = !!app.advancedOptionsTranslation
    return (
      <div
        className={classNames(
          style.appNameCell,
          !teamApplications[app.id]?.selected && style.appNameCellNotSelected,
        )}
      >
        <Tooltip
          placement="topLeft"
          overlay={t(app.description)}
          align={{ offset: [0, -5] }}
          trigger={['hover']}
          mouseLeaveDelay={0}
        >
          <span>{app.name}</span>
        </Tooltip>

        {hasAdvanced && (
          <Checkbox
            label={t('teams.showAdvanced')}
            name={'teams.showAdvanced'}
            disabled={!teamApplications[app.id]?.selected}
            classes={curr => ({
              ...curr,
              root: classNames(
                curr.root,
                style.checkboxModifications,
                !teamApplications[app.id]?.selected && commonStyle.cursorDefault,
              ),
            })}
            checked={!!teamApplications[app.id]?.showAdvanced}
            onChange={() =>
              dispatchTeamApplications({
                appId: app.id,
                showAdvanced: !teamApplications[app.id]?.showAdvanced,
              })
            }
          />
        )}
      </div>
    )
  }

  const renderAccessCell = (app: IApplication) => (
    <Tooltip
      placement="top"
      overlay={t('teams.mustHavePortalAccess')}
      align={{ offset: [0, -5] }}
      trigger={app.id === portalAppId ? ['hover'] : []}
      mouseLeaveDelay={0}
    >
      <TextToggler
        classes={curr => ({
          ...curr,
          root: classNames(curr.root, style.togglerBackground),
          toggleContainer: classNames(curr.toggleContainer, style.togglerAdjustment),
        })}
        title={''}
        selected={!!teamApplications[app.id]?.selected}
        onToggle={() => {
          if (app.id === portalAppId) {
            return
          }
          dispatchTeamApplications({
            appId: app.id,
            selected: !teamApplications[app.id]?.selected,
            role: teamApplications[app.id]?.role || app.defaultRole,
          })

          const autoSelectionToggles = autoToggleApplications?.[app.id]
          if (autoSelectionToggles) {
            for (const toggle of autoSelectionToggles) {
              if (toggle.toggledSelection === !teamApplications[app.id]?.selected) {
                for (const targetDispatch of toggle.targetDispatches) {
                  dispatchTeamApplications(targetDispatch)
                }
              }
            }
          }
        }}
      />
    </Tooltip>
  )

  const renderRoleCell = (app: IApplication) => {
    const dropdownDisabled = !teamApplications[app.id]?.selected
    const hasAdvanced = !!app.advancedOptionsTranslation

    const role = !hasAdvanced
      ? teamApplications[app.id]?.role
      : getAdvancedRole(app.advancedOptionsTranslation, teamApplications[app.id]?.features)

    const roleDisplayText = !hasAdvanced
      ? app?.roleOptions?.find(ro => ro.value === teamApplications[app.id]?.role)?.text
      : app?.advancedOptionsTranslation?.find(ao => ao.value === role)?.text

    const roleOptions = !hasAdvanced ? app?.roleOptions : app?.advancedOptionsTranslation

    return (
      <div className={style.roleWrapper}>
        {(hasAdvanced ? !teamApplications[app.id]?.showAdvanced : !!app?.roleOptions) ? (
          <Dropdown
            closeOnItemClick={true}
            classes={defaultClasses => ({
              ...defaultClasses,
              root: classNames(defaultClasses.root, style.teamRootFont),
              button: classNames(defaultClasses.button, style.dropdownItem),
              label: classNames(
                defaultClasses.label,
                style.dropdownLabel,
                !role && teamApplications[app.id]?.selected && style.dropdownLabelWarning,
              ),
              icon: classNames(defaultClasses.icon, style.dropdownCaretIcon),
            })}
            disabled={dropdownDisabled}
            label={role ? t(roleDisplayText) : t('teams.pleaseSelect')}
          >
            {roleOptions?.map(item => (
              <DropdownSelectableItem
                key={item.value}
                selected={role === item.value}
                onClick={e => {
                  if (dropdownDisabled) {
                    stopPropagationPreventDefault(e)
                  } else {
                    dispatchTeamApplications({
                      appId: app.id,
                      role: !hasAdvanced ? item.value : undefined,
                      features: hasAdvanced
                        ? [
                            ...(app?.advancedOptionsTranslation?.find(o => o.value === item.value)
                              ?.requiredOptions || []),
                          ]
                        : undefined,
                    })
                  }
                }}
              >
                {t(item.text)}
              </DropdownSelectableItem>
            ))}
          </Dropdown>
        ) : (hasAdvanced ? teamApplications[app.id]?.showAdvanced : !!app?.featuresOptions) ? (
          <Select
            multiple
            truncateMultiple
            disabled={dropdownDisabled}
            placeholder={t('teams.pleaseSelect')}
            options={(app.featuresOptions ?? [])
              .filter(item => isAppFeatureAvailable(app, item.value, company))
              .map(item => ({ value: item.value, label: t(item.text) }))}
            value={teamApplications[app.id]?.features || []}
            onChange={value => {
              const newValues = Array.isArray(value) ? value : [value]
              const oldValues = teamApplications[app.id]?.features || []
              // Select returns the raw new set; re-derive the changed item and run it
              // through getAppFeaturesValue to preserve Harvest's dependent-feature
              // rules (e.g. Nurture auto-adds Generate).
              const changed =
                newValues.find(v => !oldValues.includes(v)) ??
                oldValues.find(v => !newValues.includes(v))
              dispatchTeamApplications({
                appId: app.id,
                features: changed ? getAppFeaturesValue(app, oldValues, changed) : newValues,
              })
            }}
          />
        ) : (
          <span className={style.roleDash}>&mdash;</span>
        )}
        {!!app?.advancedInfo && (
          <Tooltip
            placement="top"
            overlay={t('applications.learnAboutApplicationRoles')}
            align={{ offset: [0, -5] }}
            trigger={['hover']}
            mouseLeaveDelay={0}
          >
            <a href={app.advancedInfo} target="_blank" className={style.infoWrapper}>
              <Icons.InfoIcon className={style.infoIconSize} />
            </a>
          </Tooltip>
        )}
      </div>
    )
  }

  const appAccessColumns: ColumnType<IApplication>[] = [
    {
      key: 'appName',
      title: t('teams.app'),
      render: renderAppNameCell,
    },
    {
      key: 'access',
      title: t('teams.access'),
      render: renderAccessCell,
    },
    {
      key: 'role',
      title: t('teams.role'),
      render: renderRoleCell,
    },
  ]

  return (
    <div className={style.editApplicationTeamRoot}>
      <div className={style.teamHeader}>
        <Heading level={3}>{action === 'add' ? t('teams.addTeam') : team?.name}</Heading>
        {action === 'edit' && team?.isLocked && (
          <Tooltip
            placement="top"
            overlay={<span>{t('teams.samlTeam')}</span>}
            align={{ offset: [0, -5] }}
          >
            <Icons.KeySolidIcon width="20px" height="20px" />
          </Tooltip>
        )}
        {action === 'edit' && !team?.isLocked && props.onDelete && (
          <IconButton small onClick={props.onDelete}>
            <Icons.TrashIcon width="20px" height="20px" color="#fa1478" />
          </IconButton>
        )}
      </div>

      <div className={style.inputRow}>
        <div className={style.inputFieldWrapper}>
          <InputField
            classes={current => ({
              ...current,
              root: classNames(current.root, style.inputFieldModifications),
            })}
            type="text"
            label={t('teams.teamName')}
            value={teamName}
            required={true}
            noClearIcon={team?.isLocked}
            messages={[
              ...teamInputFieldErrorMessages,
              {
                status: 'hint',
                message: t('teams.teamNameContains'),
              },
            ]}
            disabled={team?.isLocked}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setTeamName(e.target.value)
              setIsEdited(true)
            }}
          />
        </div>
        <div className={style.inputFieldWrapper}>
          <InputField
            classes={current => ({
              ...current,
              root: classNames(current.root, style.inputFieldModifications),
            })}
            type="text"
            label={t('teams.description')}
            value={teamDescription}
            required={false}
            noClearIcon={false}
            disabled={false}
            messages={teamDescriptionInputFieldErrorMessages}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setTeamDescription(e.target.value)
              setIsEdited(true)
            }}
          />
        </div>
      </div>

      {dependenceError && (
        <div className={style.error}>
          {t('applications.dependentAppError', {
            dependent: dependenceError.dependent,
            dependency: dependenceError.dependecy,
          })}
        </div>
      )}
      <div className={style.appAccessHeading}>{t('teams.appAccess')}</div>
      <div className={style.appAccessTableWrapper}>
        {/*
          Stable rowKey: TableV2 defaults rowKey to uniqueId() which mints a new
          key every render, remounting each row (and the feature Select) on every
          state change — which closed the multi-select dropdown mid-selection.
        */}
        <TableV2 columns={appAccessColumns} data={appAccessData} rowKey={app => app.id} />
      </div>

      <div className={style.footer}>
        <span className={style.unsavedChanges}>{isEdited && t('teams.unsavedChanges')}</span>
        <div className={style.footerActions}>
          <Button styleVariant="tertiary" disabled={!isEdited} onClick={handleCancel}>
            {t('btn.cancel')}
          </Button>
          <Button
            styleVariant="superprimary"
            disabled={isSubmitDisabled}
            onClick={e => handleSubmit(e)}
          >
            {t('btn.save')}
          </Button>
        </div>
      </div>
    </div>
  )
})
